import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search as SearchIcon, X, Loader2 } from 'lucide-react';
import { partnerApi } from '../services/partner-api';
import { handleApiError } from '../lib/api-client';
import { ResultCard } from '../components/search/ResultCard';
import { Pagination } from '../components/common/Pagination';
import type { PartnerSearchResult } from '../types';

const LIMIT = 20;

export function Search() {
  const [searchParams, setSearchParams] = useSearchParams();

  const query = searchParams.get('q') || '';
  const type = (searchParams.get('type') || 'all') as 'all' | 'business' | 'trainer';
  const offset = parseInt(searchParams.get('offset') || '0', 10);

  const [inputValue, setInputValue] = useState(query);
  const [results, setResults] = useState<PartnerSearchResult[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [hasSearched, setHasSearched] = useState(false);

  const doSearch = useCallback(async (q: string, t: string, o: number) => {
    if (q.length < 2) return;

    setIsLoading(true);
    setError('');
    try {
      const data = await partnerApi.search({
        query: q,
        type: t as 'all' | 'business' | 'trainer',
        limit: LIMIT,
        offset: o,
      });
      setResults(data.results);
      setTotal(data.total);
      setHasSearched(true);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (query.length >= 2) {
      doSearch(query, type, offset);
    }
  }, [query, type, offset, doSearch]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (inputValue.length < 2) return;
    setSearchParams({
      q: inputValue,
      type,
      offset: '0',
    });
  };

  const handleTypeChange = (newType: string) => {
    setSearchParams({
      q: query,
      type: newType,
      offset: '0',
    });
  };

  const handleClearFilters = () => {
    setInputValue('');
    setSearchParams({});
    setResults([]);
    setTotal(0);
    setHasSearched(false);
  };

  const handlePageChange = (newOffset: number) => {
    setSearchParams({
      q: query,
      type,
      offset: String(newOffset),
    });
  };

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Wyszukaj obiekt lub trenera</h1>
        <p className="text-gray-500 mt-1">Znajdz podmiot Spotto i skopiuj deep link lub pobierz QR kod</p>
      </div>

      {/* Search + Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
        <form onSubmit={handleSearch} className="flex gap-3">
          <div className="relative flex-1">
            <SearchIcon size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder="Szukaj po nazwie, NIP, firmie..."
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <button
            type="submit"
            disabled={inputValue.length < 2}
            className="px-6 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Szukaj
          </button>
        </form>

        <div className="flex items-center gap-3 mt-4">
          <span className="text-sm text-gray-500">Typ:</span>
          {(['all', 'business', 'trainer'] as const).map((t) => (
            <button
              key={t}
              onClick={() => handleTypeChange(t)}
              className={`text-sm px-3 py-1 rounded-full transition-colors ${
                type === t
                  ? 'bg-blue-100 text-blue-700 font-medium'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {t === 'all' ? 'Wszystko' : t === 'business' ? 'Obiekty' : 'Trenerzy'}
            </button>
          ))}

          {query && (
            <button
              onClick={handleClearFilters}
              className="ml-auto flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
            >
              <X size={14} />
              Wyczysc
            </button>
          )}
        </div>
      </div>

      {/* Results */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 size={24} className="animate-spin text-blue-500" />
          <span className="ml-2 text-gray-500">Szukam...</span>
        </div>
      )}

      {error && (
        <div className="bg-red-50 text-red-600 px-4 py-3 rounded-xl text-sm">
          {error}
        </div>
      )}

      {!isLoading && !error && hasSearched && results.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          <p className="text-lg font-medium">Brak wynikow</p>
          <p className="text-sm mt-1">Sprobuj innej frazy lub zmien filtry</p>
        </div>
      )}

      {!isLoading && !error && results.length > 0 && (
        <>
          <p className="text-sm text-gray-500 mb-4">
            Znaleziono {total} {total === 1 ? 'wynik' : 'wynikow'}
          </p>
          <div className="space-y-3">
            {results.map((result) => (
              <ResultCard key={`${result.entityType}-${result.id}`} result={result} />
            ))}
          </div>
          <Pagination
            total={total}
            limit={LIMIT}
            offset={offset}
            onPageChange={handlePageChange}
          />
        </>
      )}
    </div>
  );
}
